// Card data model for secure image management

export class Card {
  constructor({
    id = null,
    imageData = null,
    metadata = {},
    position = { x: 0, y: 0 },
    uploadDate = new Date().toISOString(),
    tags = []
  } = {}) {
    this.id = id || Card.generateId();
    this.imageData = imageData;
    this.metadata = {
      title: metadata.title || `Card ${this.id}`,
      description: metadata.description || '',
      row: metadata.row || null,
      originalFileName: metadata.originalFileName || null,
      fileSize: metadata.fileSize || null,
      fileType: metadata.fileType || null,
      shopifyId: metadata.shopifyId || null,
      ...metadata
    };
    this.position = { ...position };
    this.uploadDate = uploadDate;
    this.tags = [...tags];
  }

  static generateId() {
    return `card_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  // Create card from uploaded file
  static async fromFile(file, position = { x: 0, y: 0 }) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const card = new Card({
          imageData: e.target.result, // base64 data URL
          metadata: {
            originalFileName: file.name,
            fileSize: file.size,
            fileType: file.type,
            title: file.name.split('.')[0] // Remove extension
          },
          position
        });
        
        resolve(card);
      };
      
      reader.onerror = () => {
        reject(new Error(`Failed to read file: ${file.name}`));
      };
      
      reader.readAsDataURL(file);
    });
  }

  // Get color for fallback or theming
  getColor() {
    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEEAD',
      '#D4A5A5', '#9B59B6', '#3498DB', '#E74C3C', '#2ECC71',
      '#F5D76E', '#3FC380', '#8E44AD', '#3498DB', '#E08283'
    ];
    
    // Use hash of ID to get consistent color
    const hash = this.id.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    
    return colors[Math.abs(hash) % colors.length];
  }

  // Update position
  setPosition(x, y) {
    this.position = { x, y };
  }

  // Add tag
  addTag(tag) {
    if (!this.tags.includes(tag)) {
      this.tags.push(tag);
    }
  }

  // Remove tag
  removeTag(tag) {
    this.tags = this.tags.filter(t => t !== tag);
  }

  // Update metadata
  updateMetadata(updates) {
    this.metadata = { ...this.metadata, ...updates };
  }

  // Clone card
  clone() {
    return new Card({
      id: Card.generateId(),
      imageData: this.imageData,
      metadata: { ...this.metadata },
      position: { ...this.position },
      uploadDate: this.uploadDate,
      tags: [...this.tags]
    });
  }

  // Serialize for storage
  toJSON() {
    return {
      id: this.id,
      imageData: this.imageData,
      metadata: this.metadata,
      position: this.position,
      uploadDate: this.uploadDate,
      tags: this.tags
    };
  }

  // Deserialize from storage
  static fromJSON(data) {
    return new Card(data);
  }
}

// Collection management utilities
export class CardCollection {
  constructor() {
    this.cards = new Map();
    this.positions = new Map(); // position -> cardId mapping
  }

  // Add card
  addCard(card) {
    this.cards.set(card.id, card);
    this.setCardPosition(card.id, card.position.x, card.position.y);
  }

  // Remove card
  removeCard(cardId) {
    const card = this.cards.get(cardId);
    if (card) {
      this.clearPosition(card.position.x, card.position.y);
      this.cards.delete(cardId);
    }
  }

  // Get card at position
  getCardAt(x, y) {
    const posKey = `${x},${y}`;
    const cardId = this.positions.get(posKey);
    return cardId ? this.cards.get(cardId) : null;
  }

  // Set card position
  setCardPosition(cardId, x, y) {
    const card = this.cards.get(cardId);
    if (!card) return false;

    // Clear old position
    this.clearPosition(card.position.x, card.position.y);
    
    // Set new position
    const posKey = `${x},${y}`;
    this.positions.set(posKey, cardId);
    card.setPosition(x, y);
    
    return true;
  }

  // Clear position
  clearPosition(x, y) {
    const posKey = `${x},${y}`;
    this.positions.delete(posKey);
  }

  // Get all cards
  getAllCards() {
    return Array.from(this.cards.values());
  }

  // Get cards in area
  getCardsInArea(startX, startY, endX, endY) {
    const cardsInArea = [];
    for (let x = startX; x <= endX; x++) {
      for (let y = startY; y <= endY; y++) {
        const card = this.getCardAt(x, y);
        if (card) cardsInArea.push(card);
      }
    }
    return cardsInArea;
  }

  // Serialize for storage
  toJSON() {
    return {
      cards: this.getAllCards().map(card => card.toJSON()),
      version: '1.0',
      savedDate: new Date().toISOString()
    };
  }

  // Load from storage
  static fromJSON(data) {
    const collection = new CardCollection();
    if (data.cards) {
      data.cards.forEach(cardData => {
        const card = Card.fromJSON(cardData);
        collection.addCard(card);
      });
    }
    return collection;
  }
}
